import { useEffect, useState } from "react";
import clsx from "clsx";
import { Tooltip } from "react-tooltip";
import tradeSettingsStore, {
  ITradeSettingsStore,
} from "@/store/tradeSettingsStore";
import {
  setNumericSetting,
  setCPSettingNumericOverride,
} from "@/store/tradeSettingsStoreACTIONS";
import { ICPSettingsOverride } from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { resolveOverrideBorder } from "./CPSettingsGridHelper";
import { FlashKeyframes, getInputFlashStyles } from "@/lib/styles/flashStyles";
import { isIcebergReduction } from "../../helper/cellRenderers/IcebergCellRenderer/icebergHelpers";
import { generateToastMessageForTradeFlowErrors } from "@/lib/notifications/toastNotificationHelpers";
import { toast } from "react-toastify";
import {
  buildIcebergMinSizeMessage,
  findBlocksUnderIcebergMin,
  getIcebergBlocksForTrade,
} from "../../helper/cellRenderers/IcebergCellRenderer/icebergMinSizeHelpers";
import {
  flashIcebergCpCell,
  getEffectiveIcebergFloorForCP,
} from "./CPSettingsGridIcebergHelpers";

// Exported so the condensed Trade Settings rail panel
// (TradeSettingsCondensedGrid) can reuse the identical numeric control — same
// per-CP override wiring, iceberg floor enforcement, and flash behaviour.
export function SettingNumericInput({
  value,
  esid,
  settingField,
  cpCode,
  fontSizeClass,
  min,
  max,
  suffix,
  isOverridden,
  isPending,
  isFollowingPending,
  disabled,
  tooltipId,
  tooltipMessage,
}: {
  value: number | string | undefined;
  esid: string;
  settingField: keyof ICPSettingsOverride & keyof ITradeSettingsStore;
  cpCode?: string;
  fontSizeClass: string;
  min?: number;
  max?: number;
  suffix?: string;
  isOverridden?: boolean;
  isPending?: boolean;
  isFollowingPending?: boolean;
  disabled?: boolean;
  tooltipId?: string;
  tooltipMessage?: string;
}) {
  // LQC-1040: iceberg is conventionally a whole-number percentage. Round
  // when stringifying so any FP residue (e.g., 55.0000…1 from a 0.55 * 100
  // hydration that slipped through) doesn't reach the input.
  const isIcebergReleaseSizeField = settingField === "releaseSize";
  const formatForDisplay = (v: number | string | undefined): string => {
    if (v === undefined) return "";
    if (isIcebergReleaseSizeField && typeof v === "number") {
      return String(Math.round(v));
    }
    return String(v);
  };

  const [localValue, setLocalValue] = useState<string>(formatForDisplay(value));

  useEffect(() => {
    setLocalValue(formatForDisplay(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // LQC-1040: subscribe to the per-CP iceberg flash array so a rejected
  // reduction (or rejected clear) briefly flashes this cell red. cpCode
  // undefined matches the trade-level row entry.
  const icebergFlashEntries = tradeSettingsStore(
    (state) => state.icebergCpCellsToFlash,
  );
  const isFlashing =
    isIcebergReleaseSizeField &&
    icebergFlashEntries.some(
      (f) =>
        f.esid === esid && f.cpCode === cpCode && f.field === "releaseSize",
    );

  const commitValue = (numValue: number) => {
    if (min !== undefined && numValue < min) numValue = min;
    if (max !== undefined && numValue > max) numValue = max;
    // LQC-1040: iceberg is conventionally a whole-number percentage —
    // round before persisting so we never store fractional values.
    if (isIcebergReleaseSizeField) numValue = Math.round(numValue);

    // LQC-1040: in Edit mode, reject any per-CP releaseSize edit that would
    // go below the CP's effective floor — the highest value the bank has
    // seen for that CP. CPs without an explicit baseline still have an
    // implicit floor at the trade-level default (see helper). Trade-level
    // releaseSize edits are not enforced here per option (i).
    if (isIcebergReleaseSizeField && cpCode) {
      const state = tradeSettingsStore.getState();
      const editBaseline = state.editBaselineOverrides[esid];
      const isApiKnownCp = (state.editKnownCpCodes[esid] ?? []).includes(
        cpCode,
      );
      const floor = getEffectiveIcebergFloorForCP(
        editBaseline,
        cpCode,
        isApiKnownCp,
      );
      if (floor !== undefined && isIcebergReduction(numValue, floor)) {
        generateToastMessageForTradeFlowErrors({
          action: "icebergSize",
          outcome: "attemptedReduction",
        });
        flashIcebergCpCell(esid, cpCode);
        // Reset the input to the current displayed value so the user's typed
        // text doesn't linger. We don't snap them to the baseline — they may
        // still want to be at whatever staged value they had before.
        setLocalValue(value !== undefined ? String(value) : "");
        return;
      }
    }

    // LQC-2874: reject an iceberg that would release a block under 100,000.
    if (isIcebergReleaseSizeField) {
      const blocks = getIcebergBlocksForTrade(esid);
      if (findBlocksUnderIcebergMin(blocks, numValue).length) {
        toast.error(buildIcebergMinSizeMessage(blocks));
        flashIcebergCpCell(esid, cpCode);
        setLocalValue(formatForDisplay(value));
        return;
      }
    }

    setLocalValue(String(numValue));
    if (cpCode) {
      setCPSettingNumericOverride({
        esid,
        cpCode,
        settingField,
        value: numValue,
      });
    } else {
      setNumericSetting({ esid, settingField, value: numValue });
    }
  };

  const handleBlur = () => {
    const numValue = Number(localValue);
    if (localValue === "" || isNaN(numValue)) {
      setLocalValue(value !== undefined ? String(value) : "");
      return;
    }
    commitValue(numValue);
  };

  const borderColor = resolveOverrideBorder(
    isOverridden,
    isPending,
    isFollowingPending,
  );
  const flashStyles = getInputFlashStyles(isFlashing);

  const showTooltip = !!tooltipId && !!tooltipMessage;

  return (
    <>
      <FlashKeyframes isFlashing={isFlashing} />
      <div
        data-tooltip-id={showTooltip ? tooltipId : undefined}
        className={clsx(
          "flex h-[28px] w-full items-center rounded-md border dark:bg-gray-800",
          !borderColor && !isFlashing && "border-gray-300 dark:border-gray-600",
          disabled && "cursor-not-allowed opacity-50",
        )}
        style={
          isFlashing ? flashStyles : borderColor ? { borderColor } : undefined
        }
      >
        <input
          type="text"
          inputMode="decimal"
          disabled={disabled}
          className={clsx(
            "h-full w-full bg-transparent px-2 text-center outline-none dark:text-gray-200",
            disabled && "cursor-not-allowed",
            fontSizeClass,
          )}
          value={localValue}
          onChange={(e) => {
            if (/^\d*\.?\d*$/.test(e.target.value)) {
              setLocalValue(e.target.value);
            }
          }}
          onBlur={handleBlur}
          onKeyDownCapture={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
              e.stopPropagation();
              const current = Number(localValue) || 0;
              const step = e.key === "ArrowUp" ? 1 : -1;
              let next = current + step;
              if (min !== undefined && next < min) next = min;
              if (max !== undefined && next > max) next = max;
              setLocalValue(String(next));
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
        {suffix && (
          <span className={`${fontSizeClass} pr-2 text-gray-500`}>
            {suffix}
          </span>
        )}
      </div>
      {showTooltip && (
        <Tooltip
          id={tooltipId}
          place="top"
          delayShow={100}
          className="z-50 !text-xs"
        >
          {tooltipMessage}
        </Tooltip>
      )}
    </>
  );
}

export default SettingNumericInput;
